import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import MarkEmailReadOutlined from "@mui/icons-material/MarkEmailReadOutlined";
import PersonAddAltOutlined from "@mui/icons-material/PersonAddAltOutlined";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { CardBackButton } from "/imports/ui/components/shared";

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

function ForgotPasswordView() {
  const navigate = useNavigate();
  const { slug } = useWidget();
  const { forgotPassword } = useAuth();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const data = await forgotPassword(email.trim(), slug);
      setStatus(data?.status || "link_sent");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <Card sx={{ maxWidth: "25rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          <CardBackButton onClick={() => navigate(`/${slug}/login`)} />
          {status === "not_found" ? (
            <Box textAlign="center">
              <PersonAddAltOutlined color="primary" sx={{ fontSize: "3.5rem", mb: 1 }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                No account found
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                We don't have an account for <strong>{email.trim()}</strong>. It looks like you haven't signed up yet — create an account to get
                started.
              </Typography>
              <Button component={RouterLink} to={`/${slug}/signup`} variant="contained" fullWidth size="large" sx={{ mb: 1 }}>
                Sign up
              </Button>
              <Button onClick={() => setStatus(null)} fullWidth size="large">
                Try a different email
              </Button>
            </Box>
          ) : status === "link_sent" ? (
            <Box textAlign="center">
              <MarkEmailReadOutlined color="primary" sx={{ fontSize: "3.5rem", mb: 1 }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Check your email
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                We've sent a link to reset your password to <strong>{email.trim()}</strong>. The link may take a minute to arrive — remember to check
                your spam folder.
              </Typography>
              <Button component={RouterLink} to={`/${slug}/login`} variant="contained" fullWidth size="large">
                Back to sign in
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
                Forgot password
              </Typography>
              <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
                Enter your email and we'll send you a link to reset your password
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  autoComplete="email"
                  autoFocus
                />

                <Button type="submit" variant="contained" fullWidth size="large" disabled={loading || !isValidEmail(email)} sx={{ mt: 3, mb: 2 }}>
                  {loading ? "Sending..." : "Send reset link"}
                </Button>
              </form>

              <Typography variant="body2" textAlign="center">
                Remembered your password?{" "}
                <Link component={RouterLink} to={`/${slug}/login`}>
                  Sign in
                </Link>
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ForgotPasswordView;
